import React, { useState, useEffect, useRef } from 'react';
import { RotateCw, FileText, Check, Circle } from 'lucide-react';
import { API_BASE } from '../apiConfig';

const styles = {
  container: {
    padding: '20px 0',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    marginBottom: '24px',
    position: 'relative',
  },
  stats: {
    display: 'inline-flex',
    alignItems: 'center',
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    overflow: 'hidden',
    height: '36px',
    paddingRight: '16px',
    boxShadow: '0 4px 10px rgba(0,0,0,0.04)',
  },
  statsIcon: {
    background: '#88d645',
    width: '44px',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    position: 'relative',
    marginRight: '12px',
    clipPath: 'polygon(0% 0%, 85% 0%, 100% 50%, 85% 100%, 0% 100%)',
  },
  statsText: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '14px',
    color: '#1e293b',
    fontWeight: 700,
  },
  statsSize: {
    whiteSpace: 'nowrap',
  },
  statsSep: {
    width: '1px',
    height: '18px',
    background: '#e2e8f0',
  },
  statsCount: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: '#1e293b',
    fontWeight: 500,
  },
  statsCountNum: {
    color: '#88d645',
    fontWeight: 700,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '20px',
    marginBottom: '24px',
  },
  card: (isSelected) => ({
    background: 'var(--surface)',
    border: isSelected ? '2px solid #88d645' : '1px solid var(--border)',
    borderRadius: '12px',
    padding: '12px',
    position: 'relative',
    transition: 'transform 0.15s, border-color 0.15s, box-shadow 0.15s',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: '240px',
    cursor: 'pointer',
    boxShadow: isSelected ? '0 8px 20px rgba(136, 214, 69, 0.12)' : 'none',
  }),
  thumbnail: {
    width: '100%',
    aspectRatio: '1 / 1.41',
    background: 'var(--surface2)',
    borderRadius: '6px',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    border: '1px solid var(--border)',
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  thumbImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  pageLabel: {
    fontSize: '12px',
    color: 'var(--text)',
    textAlign: 'center',
    fontWeight: 600,
  },
  actions: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    display: 'flex',
    gap: '6px',
    zIndex: 10,
  },
  actionBtn: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: 'var(--surface)',
    border: '1px solid var(--border-md)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'var(--text-muted)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'background 0.2s, color 0.2s',
  },
  selectBadge: (isSelected) => ({
    position: 'absolute',
    top: '12px',
    left: '12px',
    zIndex: 10,
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    background: isSelected ? '#88d645' : '#fff',
    border: isSelected ? 'none' : '2px solid #cbd5e1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    transition: 'background 0.2s, border-color 0.2s',
  })
};

const Thumbnail = ({ file, pageIndex, rotation }) => {
  const [thumb, setThumb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' } // Load slightly before they enter view
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let active = true;
    const fetchThumb = async () => {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('page', pageIndex);
        
        const res = await fetch(`${API_BASE}/render-pdf`, {
          method: 'POST',
          body: formData,
        });
        
        if (res.ok && active) {
          const blob = await res.blob();
          setThumb(URL.createObjectURL(blob));
        }
      } catch (e) {
        console.error("Thumb failed", e);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchThumb();
    return () => { active = false; if (thumb) URL.revokeObjectURL(thumb); };
  }, [file, pageIndex, isVisible]);

  return (
    <div ref={containerRef} style={{ ...styles.thumbnail, transform: `rotate(${rotation}deg)` }}>
      {loading ? (
        <RotateCw className="animate-spin" size={24} color="var(--text-muted)" />
      ) : thumb ? (
        <img src={thumb} alt={`Page ${pageIndex + 1}`} style={styles.thumbImg} />
      ) : (
        <FileText size={48} color="var(--border-hi)" />
      )}
    </div>
  );
};

const PageCard = ({ file, pageIndex, rotation, isSelected, onToggle, onRotate }) => {
  return (
    <div 
      style={styles.card(isSelected)}
      onClick={onToggle}
    >
      <div style={styles.selectBadge(isSelected)}>
        {isSelected && <Check size={14} strokeWidth={4} />}
      </div>
      
      <div style={styles.actions}>
        <div 
          style={styles.actionBtn} 
          onClick={(e) => { e.stopPropagation(); onRotate(); }}
          title="Rotate 90°"
        >
          <RotateCw size={14} />
        </div>
      </div>
      <Thumbnail file={file} pageIndex={pageIndex} rotation={rotation} />
      <div style={styles.pageLabel}>Page {pageIndex + 1}</div>
    </div>
  );
};

export default function PDFSplit({ file, rotations, onRotatePage, selectedPages, setSelectedPages }) {
  const [pageCount, setPageCount] = useState(0);

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`${API_BASE}/convert/inspect`, {
          method: 'POST',
          body: formData,
        });
        if (res.ok) {
          const data = await res.json();
          setPageCount(data.totalPages);
          if (selectedPages.length === 0) {
            setSelectedPages(Array.from({ length: data.totalPages }, (_, i) => i));
          }
        }
      } catch (e) {}
    };
    fetchInfo();
  }, [file]);

  const togglePage = (idx) => {
    setSelectedPages(prev => 
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx].sort((a,b) => a-b)
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.stats}>
          <div style={styles.statsIcon}>
            <FileText size={18} strokeWidth={2.5} />
          </div>
          <div style={styles.statsText}>
            <span style={styles.statsSize}>{(file.size / (1024*1024)).toFixed(2)} MB</span>
            <div style={styles.statsSep} />
            <div style={styles.statsCount}>
              <span style={styles.statsCountNum}>{selectedPages.length}</span>
              <span>/ {pageCount} Pages Selected</span>
            </div>
          </div>
        </div>
      </div>

      <div style={styles.grid}>
        {Array.from({ length: pageCount }).map((_, idx) => (
          <PageCard
            key={`page-${idx}`}
            file={file}
            pageIndex={idx}
            rotation={rotations[idx] || 0}
            isSelected={selectedPages.includes(idx)}
            onToggle={() => togglePage(idx)}
            onRotate={() => onRotatePage(idx)}
          />
        ))}
      </div>
    </div>
  );
}
